// Maximum safe URL length (modern browsers support 8000+ chars)
import { ALTERNATE_MEXICANO_PAIRING, normalizeMexicanoPairing } from './mexicano';
import { partnershipKey } from './partnershipKey';

export const MAX_URL_LENGTH = 6000;

// Unicode-safe base64 helpers — btoa() only handles Latin-1, so we go through
// UTF-8 bytes first so player names in any script (Cyrillic, Arabic, CJK…) work.
const toBase64 = (str) => {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
};

// Backward-compatible: old ASCII-only states decode fine because ASCII is a
// valid subset of UTF-8 and TextDecoder produces the same string as plain atob.
const fromBase64 = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
};

// LocalStorage key for tournament state
export const getTournamentStorageKey = (name) => `tournament-${encodeURIComponent(name || '')}`;

// Save tournament state to localStorage
export const saveToLocalStorage = (name, state) => {
  try {
    const encoded = encodeState(state);
    localStorage.setItem(getTournamentStorageKey(name), encoded);
    return true;
  } catch (error) {
    console.warn('Unable to save tournament state to localStorage', error);
    return false;
  }
};

// Load tournament state from localStorage
export const loadFromLocalStorage = (name) => {
  try {
    const encoded = localStorage.getItem(getTournamentStorageKey(name));
    if (!encoded) return null;
    return decodeState(encoded);
  } catch (error) {
    console.warn('Unable to load tournament state from localStorage', error);
    return null;
  }
};

// Clear tournament state from localStorage
export const clearFromLocalStorage = (name) => {
  try {
    localStorage.removeItem(getTournamentStorageKey(name));
  } catch (error) {
    console.warn('Unable to clear tournament state from localStorage', error);
  }
};

// Check if generated share URL would be too long
export const isShareUrlTooLong = (encodedState) => {
  // Estimate URL length: base URL + path + "?state=" + encoded state
  const estimatedLength = window.location.origin.length + 100 + 7 + encodeURIComponent(encodedState).length;
  return estimatedLength > MAX_URL_LENGTH;
};

export const encodeState = (state) => {
  const playerIndex = new Map(state.players.map((player, idx) => [player, idx]));
  const playerGenders = state.playerGenders || {};

  const encodePartnerships = (pairs) => {
    if (!Array.isArray(pairs)) return undefined;
    const encoded = [];
    for (const pair of pairs) {
      if (!Array.isArray(pair) || pair.length !== 2) continue;
      const i1 = playerIndex.get(pair[0]);
      const i2 = playerIndex.get(pair[1]);
      if (typeof i1 !== 'number' || typeof i2 !== 'number') continue;
      encoded.push([i1, i2]);
    }
    return encoded.length ? encoded : undefined;
  };

  const encodeCountMap = (countMap) => {
    if (!countMap || typeof countMap !== 'object') return undefined;
    const arr = state.players.map((p) => {
      const v = countMap[p];
      return typeof v === 'number' ? v : 0;
    });
    return arr.some((v) => v !== 0) ? arr : undefined;
  };

  const encodeGames = (games) => {
    if (!Array.isArray(games)) return undefined;
    const encoded = games.map(game => {
      if (!game) return null;
      return {
        c: game.courtId,
        t1: {
          p: Array.isArray(game.team1?.players) ? game.team1.players.map(p => playerIndex.get(p)) : [],
          s: game.team1?.score
        },
        t2: {
          p: Array.isArray(game.team2?.players) ? game.team2.players.map(p => playerIndex.get(p)) : [],
          s: game.team2?.score
        }
      };
    }).filter(Boolean);
    return encoded.length ? encoded : undefined;
  };

  const encodePlayerList = (list) => {
    if (!Array.isArray(list)) return undefined;
    const arr = list
      .map((p) => playerIndex.get(p))
      .filter((idx) => typeof idx === 'number');
    return arr.length ? arr : undefined;
  };

  const encodePartnershipPlayCounts = (countMap) => {
    if (!countMap || typeof countMap !== 'object') return undefined;

    const candidatePairs = [
      ...(Array.isArray(state.allPartnerships) ? state.allPartnerships : []),
      ...(Array.isArray(state.usedPartnerships) ? state.usedPartnerships : []),
      ...(Array.isArray(state.lastRoundPartnerships) ? state.lastRoundPartnerships : []),
    ];
    const seen = new Set();
    const encoded = [];

    candidatePairs.forEach((pair) => {
      if (!Array.isArray(pair) || pair.length !== 2) return;
      const key = partnershipKey(pair);
      if (seen.has(key)) return;
      seen.add(key);

      const count = countMap[key];
      const first = playerIndex.get(pair[0]);
      const second = playerIndex.get(pair[1]);
      if (typeof count === 'number' && count > 0 && typeof first === 'number' && typeof second === 'number') {
        encoded.push([first, second, count]);
      }
    });

    return encoded.length ? encoded : undefined;
  };

  // Create a more efficient representation for URLs
  const playerStatus = state.playerStatus || {};
  const compactState = {
    n: state.name, // tournament name
    fid: state.firebaseId || undefined, // stable organizer identity across reloads
    p: state.players.map((name, idx) => ({ 
      i: idx, 
      n: name, 
      g: playerGenders[name] === 'female' ? 'f' : playerGenders[name] === 'male' ? 'm' : undefined,
      a: playerStatus[name] === 'inactive' ? 0 : undefined // active by default, only store inactive
    })), // players with indices
    c: state.courts, // number of courts
    f: state.format, // tournament format (e.g., 'americano', 'mexicano')
    mx: state.mexicanoPairing === ALTERNATE_MEXICANO_PAIRING ? '14' : undefined,
    mp: state.mixedPairs ? 1 : undefined,
    ic: state.isComplete ? 1 : undefined, // tournament completion flag
    g: encodeGames(state.games),
    s: state.scores, // player scores
    pc: state.playCount, // Add play count to compact state
    pco: encodeCountMap(state.playCountOffset), // Play count offset for late joiners
    // Match results
    w: encodeCountMap(state.wins),
    l: encodeCountMap(state.losses),
    d: encodeCountMap(state.draws),
    r: state.round, // round number
    rt: state.roundType, // 'points' | 'time'
    tp: state.targetPoints,
    rm: state.roundMinutes,

    // Round history (optional; used for round navigation/edit)
    rh: Array.isArray(state.roundHistory)
      ? state.roundHistory
          .map((entry) => ({
            r: entry?.round,
            g: encodeGames(entry?.games),
            b: encodePlayerList(entry?.benchPlayers)
          }))
          .filter((entry) => entry && typeof entry.r === 'number' && Array.isArray(entry.g))
      : undefined,

    // Pairing history (optional; improves determinism across refresh/share links)
    ap: encodePartnerships(state.allPartnerships),
    up: encodePartnerships(state.usedPartnerships),
    lrp: encodePartnerships(state.lastRoundPartnerships),
    ppc: encodePartnershipPlayCounts(state.partnershipPlayCount),
    bc: encodeCountMap(state.benchCount),
    // Last game scores for TV mode display
    lgs: encodeCountMap(state.lastGameScores),
    // Timer state for TV mode
    tl: state.timeLeft,
    re: state.roundEnded ? 1 : undefined,
    // Completed tournaments keep their final courts available to Firebase TV mode.
    lg: encodeGames(state.lastGames),
    lb: encodePlayerList(state.lastBenchPlayers)
  };

  return toBase64(JSON.stringify(compactState));
};

// Smaller snapshot used for finished-tournament TV links: keep only leaderboard essentials
export const encodeFinalTvState = (state) => {
  const playerIndex = new Map((state.players || []).map((player, idx) => [player, idx]));

  const encodeCountMap = (countMap) => {
    if (!countMap || typeof countMap !== 'object' || playerIndex.size === 0) return undefined;
    const arr = state.players.map((p) => {
      const v = countMap[p];
      return typeof v === 'number' ? v : 0;
    });
    return arr.some((v) => v !== 0) ? arr : undefined;
  };

  return toBase64(JSON.stringify({
    n: state.name,
    p: (state.players || []).map((name, idx) => ({ i: idx, n: name })),
    f: state.format,
    mx: state.mexicanoPairing === ALTERNATE_MEXICANO_PAIRING ? '14' : undefined,
    rt: state.roundType,
    tp: state.targetPoints,
    rm: state.roundMinutes,
    ic: 1, // finished snapshot
    r: state.round,
    s: state.scores,
    pc: state.playCount,
    w: encodeCountMap(state.wins),
    l: encodeCountMap(state.losses),
    d: encodeCountMap(state.draws),
    lgs: encodeCountMap(state.lastGameScores)
  }));
};

export const decodeState = (encodedState) => {
  try {
    const compactState = JSON.parse(fromBase64(encodedState));

    const safePlayerName = (idx) => {
      if (!compactState?.p || typeof idx !== 'number') return null;
      const entry = compactState.p[idx];
      return entry?.n ?? null;
    };

    const decodePartnerships = (encoded) => {
      if (!Array.isArray(encoded)) return undefined;
      const decoded = [];
      for (const pair of encoded) {
        if (!Array.isArray(pair) || pair.length !== 2) continue;
        const p1 = safePlayerName(pair[0]);
        const p2 = safePlayerName(pair[1]);
        if (!p1 || !p2) continue;
        decoded.push([p1, p2]);
      }
      return decoded;
    };

    const decodeCountArray = (arr) => {
      if (!Array.isArray(arr) || !Array.isArray(compactState?.p)) return undefined;
      const map = {};
      for (let idx = 0; idx < arr.length; idx++) {
        const name = safePlayerName(idx);
        if (!name) continue;
        const value = arr[idx];
        map[name] = typeof value === 'number' ? value : 0;
      }
      return map;
    };

    const decodeGames = (encoded) => {
      if (!Array.isArray(encoded) || !Array.isArray(compactState?.p)) return undefined;
      return encoded
        .map((game) => {
          if (!game) return null;
          const team1Players = Array.isArray(game.t1?.p)
            ? game.t1.p.map((idx) => safePlayerName(idx)).filter(Boolean)
            : [];
          const team2Players = Array.isArray(game.t2?.p)
            ? game.t2.p.map((idx) => safePlayerName(idx)).filter(Boolean)
            : [];
          return {
            courtId: game.c,
            team1: { players: team1Players, score: game.t1?.s ?? null },
            team2: { players: team2Players, score: game.t2?.s ?? null }
          };
        })
        .filter(Boolean);
    };

    const decodePlayerList = (arr) => {
      if (!Array.isArray(arr)) return undefined;
      return arr.map((idx) => safePlayerName(idx)).filter(Boolean);
    };

    const decodePartnershipPlayCounts = (rows) => {
      if (!Array.isArray(rows)) return undefined;
      const counts = {};
      rows.forEach((row) => {
        if (!Array.isArray(row) || row.length !== 3) return;
        const first = safePlayerName(row[0]);
        const second = safePlayerName(row[1]);
        const count = row[2];
        if (!first || !second || typeof count !== 'number' || count <= 0) return;
        counts[partnershipKey([first, second])] = count;
      });
      return Object.keys(counts).length ? counts : undefined;
    };
    
    const playerGenders = {};
    const playerStatus = {};
    if (Array.isArray(compactState?.p)) {
      compactState.p.forEach((entry) => {
        const gender = entry?.g === 'f' ? 'female' : entry?.g === 'm' ? 'male' : undefined;
        if (entry?.n && gender) {
          playerGenders[entry.n] = gender;
        }
        // Decode player status (active by default, inactive if a === 0)
        if (entry?.n) {
          playerStatus[entry.n] = entry?.a === 0 ? 'inactive' : 'active';
        }
      });
    }

    // Rebuild full state
    const format = compactState.f || 'americano';
    return {
      name: compactState.n,
      firebaseId: typeof compactState.fid === 'string' ? compactState.fid : undefined,
      players: compactState.p.map(p => p.n),
      playerStatus: playerStatus,
      courts: compactState.c,
      format: format,
      mexicanoPairing: normalizeMexicanoPairing(compactState.mx === '14' ? ALTERNATE_MEXICANO_PAIRING : undefined),
      mixedPairs: Boolean(compactState.mp),
      roundType: compactState.rt || 'points',
      targetPoints: compactState.tp,
      roundMinutes: compactState.rm,
      isComplete: Boolean(compactState.ic),
      games: decodeGames(compactState.g),
      scores: compactState.s,
      playCount: compactState.pc, // Decode play count from compact state
      playCountOffset: decodeCountArray(compactState.pco), // Decode play count offset for late joiners
      wins: decodeCountArray(compactState.w),
      losses: decodeCountArray(compactState.l),
      draws: decodeCountArray(compactState.d),
      round: compactState.r,

      roundHistory: Array.isArray(compactState.rh)
        ? compactState.rh
            .map((entry) => ({
              round: entry?.r,
              games: decodeGames(entry?.g) || [],
              benchPlayers: decodePlayerList(entry?.b) || []
            }))
            .filter((entry) => typeof entry.round === 'number')
        : undefined,

      // Pairing history (optional)
      allPartnerships: decodePartnerships(compactState.ap),
      usedPartnerships: decodePartnerships(compactState.up),
      lastRoundPartnerships: decodePartnerships(compactState.lrp),
      partnershipPlayCount: decodePartnershipPlayCounts(compactState.ppc),
      benchCount: decodeCountArray(compactState.bc),
      // Last game scores for TV mode display
      lastGameScores: decodeCountArray(compactState.lgs),
      // Timer state for TV mode
      timeLeft: typeof compactState.tl === 'number' ? compactState.tl : null,
      roundEnded: Boolean(compactState.re),
      playerGenders: playerGenders,
      lastGames: decodeGames(compactState.lg),
      lastBenchPlayers: decodePlayerList(compactState.lb)
    };
  } catch (e) {
    console.error('Error decoding state:', e);
    return null;
  }
};
